import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  DollarSign,
  Calendar,
  MapPin,
  X,
  CheckCircle,
  AlertCircle,
  Building,
  BookOpen,
  GraduationCap,
  TrendingUp
} from 'lucide-react';

export default function UnitCostManagement() {
  const [unitCosts, setUnitCosts] = useState([]);
  const [units, setUnits] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [unitFilter, setUnitFilter] = useState('');
  const [campusFilter, setCampusFilter] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [selectedCost, setSelectedCost] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Create form state
  const [createForm, setCreateForm] = useState({
    unitId: '',
    campusId: '',
    cost: '',
    currency: 'KES',
    isFree: false,
    freeDescription: '',
    effectiveFrom: '',
    effectiveTo: '',
    lecturerPercentage: '60.00'
  });

  // Bulk create form state
  const [bulkForm, setBulkForm] = useState({
    unitIds: [],
    campusIds: [],
    cost: '',
    currency: 'KES',
    isFree: false,
    freeDescription: '',
    effectiveFrom: '',
    effectiveTo: '',
    selectAllUnits: false,
    lecturerPercentage: '60.00'
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch all data in parallel
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const [costsResponse, unitsResponse, campusesResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/unit-costs`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch(`${API_BASE_URL}/units`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch(`${API_BASE_URL}/campuses`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      ]);

      if (!costsResponse.ok) throw new Error('Failed to fetch unit costs');

      const costsData = await costsResponse.json();
      const unitsData = unitsResponse.ok ? await unitsResponse.json() : [];
      const campusesData = campusesResponse.ok ? await campusesResponse.json() : [];

      setUnitCosts(costsData);
      setUnits(unitsData);
      setCampuses(campusesData);

    } catch (error) {
      setError(error.message);
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateCost = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/unit-costs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...createForm,
          unitId: parseInt(createForm.unitId),
          campusId: parseInt(createForm.campusId),
          cost: parseFloat(createForm.cost),
          effectiveFrom: createForm.effectiveFrom || new Date().toISOString().split('T')[0],
          effectiveTo: createForm.effectiveTo || null
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create unit cost');
      }

      const newCost = await response.json();
      setUnitCosts(prev => [...prev, newCost]);
      setCreateForm({
        unitId: '',
        campusId: '',
        isFree: false,
        freeDescription: '',
        effectiveFrom: '',
        effectiveTo: '',
        lecturerPercentage: '60.00'
      });
      setShowCreateForm(false);

    } catch (error) {
      setError('Failed to create unit cost: ' + error.message);
      console.error('Error creating unit cost:', error);
    }
  };

  const handleBulkCreate = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');

      // Determine which units to process
      const unitsToProcess = bulkForm.selectAllUnits ? units : units.filter(unit => bulkForm.unitIds.includes(unit.id.toString()));

      if (unitsToProcess.length === 0) {
        alert('Please select at least one unit or check "Select All Units"');
        return;
      }

      if (bulkForm.campusIds.length === 0) {
        alert('Please select at least one campus');
        return;
      }

      let totalCreated = 0;
      let totalErrors = 0;

      // Create pricing for each unit-campus combination
      for (const unit of unitsToProcess) {
        for (const campusId of bulkForm.campusIds) {
          try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
            const response = await fetch(`${API_BASE_URL}/unit-costs`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                unitId: unit.id,
                campusId: parseInt(campusId),
                cost: parseFloat(bulkForm.cost),
                currency: bulkForm.currency,
                effectiveFrom: bulkForm.effectiveFrom || new Date().toISOString().split('T')[0],
                effectiveTo: bulkForm.effectiveTo || null,
                lecturerPercentage: parseFloat(bulkForm.lecturerPercentage) || 60.00
              }),
            });

            if (response.ok) {
              const newCost = await response.json();
              setUnitCosts(prev => [...prev, newCost]);
              totalCreated++;
            } else {
              const errorData = await response.json();
              console.error(`Error creating pricing for unit ${unit.name}:`, errorData.message);
              totalErrors++;
            }
          } catch (error) {
            console.error(`Error creating pricing for unit ${unit.name}:`, error);
            totalErrors++;
          }
        }
      }

      alert(`Created ${totalCreated} unit costs. ${totalErrors} errors.`);

      setBulkForm({
        unitIds: [],
        campusIds: [],
        cost: '',
        currency: 'KES',
        isFree: false,
        freeDescription: '',
        effectiveFrom: '',
        effectiveTo: '',
        selectAllUnits: false,
        lecturerPercentage: '60.00'
      });
      setShowBulkForm(false);

    } catch (error) {
      setError('Failed to create unit costs: ' + error.message);
      console.error('Error creating unit costs:', error);
    }
  };

  const handleEditCost = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/unit-costs/${selectedCost.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          cost: selectedCost.isFree ? 0 : parseFloat(selectedCost.cost),
          currency: selectedCost.isFree ? 'FREE' : (selectedCost.currency === 'FREE' || selectedCost.currency === 'FRE' ? 'KES' : selectedCost.currency),
          isFree: selectedCost.isFree,
          freeDescription: selectedCost.isFree ? selectedCost.freeDescription : null,
          effectiveFrom: selectedCost.effectiveFrom,
          effectiveTo: selectedCost.effectiveTo,
          lecturerPercentage: parseFloat(selectedCost.lecturerPercentage)
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update unit cost');
      }

      const updatedCost = await response.json();
      setUnitCosts(prev => prev.map(cost =>
        cost.id === updatedCost.id ? updatedCost : cost
      ));
      setShowEditModal(false);
      setSelectedCost(null);

    } catch (error) {
      setError('Failed to update unit cost: ' + error.message);
      console.error('Error updating unit cost:', error);
    }
  };

  const handleDeleteCost = async (costId) => {
    if (!window.confirm('Are you sure you want to delete this pricing record?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/unit-costs/${costId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete unit cost');
      }

      setUnitCosts(prev => prev.filter(cost => cost.id !== costId));

    } catch (error) {
      setError('Failed to delete unit cost: ' + error.message);
      console.error('Error deleting unit cost:', error);
    }
  };

  // Filter costs based on search and filters
  const filteredCosts = unitCosts.filter(cost => {
    const unit = units.find(u => u.id === cost.unitId);
    const campus = campuses.find(c => c.id === cost.campusId);

    const matchesSearch = !searchTerm ||
      (unit && unit.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (campus && campus.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesUnit = unitFilter === '' || cost.unitId === parseInt(unitFilter);
    const matchesCampus = campusFilter === '' || cost.campusId === parseInt(campusFilter);

    return matchesSearch && matchesUnit && matchesCampus;
  });

  // Group costs by unit for better organization (when no specific campus is selected)
  const groupedCosts = filteredCosts.reduce((acc, cost) => {
    const unit = units.find(u => u.id === cost.unitId);
    const unitName = unit?.name || 'Unknown Unit';

    if (!acc[unitName]) {
      acc[unitName] = [];
    }
    acc[unitName].push(cost);
    return acc;
  }, {});

  // Determine display mode: grouped by unit or single campus view
  const isCampusSpecificView = campusFilter !== '';
  const displayData = isCampusSpecificView ? filteredCosts : Object.entries(groupedCosts);

  const formatCurrency = (amount, currency, isFree) => {
    if (isFree || currency === 'FREE' || currency === 'FRE') return 'FREE';
    return `${currency} ${parseFloat(amount).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Ongoing';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-green-600 to-blue-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center">
              <DollarSign className="mr-3 h-8 w-8" />
              Unit Cost Management
            </h1>
            <p className="mt-2 text-green-100">
              Set and manage pricing for course units across campuses
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{unitCosts.length}</div>
            <div className="text-green-100">Pricing Records</div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-red-600 text-sm">{error}</div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Controls Section */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search units or campuses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <select
              value={unitFilter}
              onChange={(e) => setUnitFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">All Units</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>{unit.name}</option>
              ))}
            </select>

            <select
              value={campusFilter}
              onChange={(e) => setCampusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">All Campuses</option>
              {campuses.map((campus) => (
                <option key={campus.id} value={campus.id}>{campus.name}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowBulkForm(true)}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Building className="h-4 w-4 mr-2" />
              Bulk Set
            </button>
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Pricing
            </button>
          </div>
        </div>
      </div>

      {/* Dynamic Display: Grouped by Unit or Single Campus View */}
      {isCampusSpecificView ? (
        /* Campus-Specific View */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Campus Header */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center text-white mr-3">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {campuses.find(c => c.id === parseInt(campusFilter))?.name || 'Selected Campus'}
                  </h3>
                  <p className="text-sm text-gray-600">{filteredCosts.length} unit{filteredCosts.length !== 1 ? 's' : ''} with pricing</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {formatCurrency(filteredCosts.reduce((sum, cost) => sum + parseFloat(cost.cost), 0), filteredCosts[0]?.currency || 'KES')}
                  </div>
                  <div className="text-xs text-gray-500">Total Value</div>
                </div>
                <TrendingUp className="h-5 w-5 text-green-500" />
              </div>
            </div>
          </div>

          {/* Campus Pricing Table */}
          <div className="bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cost
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Effective Dates
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tutor %
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCosts.map((cost, index) => {
                    const unit = units.find(u => u.id === cost.unitId);

                    return (
                      <tr key={cost.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white mr-3">
                              <BookOpen className="h-4 w-4" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {unit?.name || 'Unknown Unit'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {unit?.description || ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`w-8 h-8 bg-gradient-to-r ${cost.isFree ? 'from-purple-500 to-indigo-600' : 'from-green-500 to-emerald-600'} rounded-lg flex items-center justify-center text-white mr-3`}>
                              {cost.isFree ? <CheckCircle className="h-4 w-4" /> : <DollarSign className="h-4 w-4" />}
                            </div>
                            <div>
                              <div className={`text-sm font-semibold ${cost.isFree ? 'text-indigo-600' : 'text-gray-900'}`}>
                                {formatCurrency(cost.cost, cost.currency, cost.isFree)}
                              </div>
                              {cost.isFree ? (
                                <div className="text-xs text-gray-500 italic max-w-[150px] truncate" title={cost.freeDescription}>
                                  {cost.freeDescription || 'No description'}
                                </div>
                              ) : (
                                <div className="text-xs text-gray-500 uppercase">
                                  {cost.currency}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <div className="flex items-center mb-1">
                              <Calendar className="h-4 w-4 mr-2 text-green-500" />
                              <span>From: {formatDate(cost.effectiveFrom)}</span>
                            </div>
                            {cost.effectiveTo && (
                              <div className="flex items-center">
                                <Calendar className="h-4 w-4 mr-2 text-red-500" />
                                <span>To: {formatDate(cost.effectiveTo)}</span>
                              </div>
                            )}
                            {!cost.effectiveTo && (
                              <span className="text-xs text-green-600 font-medium">Ongoing</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-lg w-fit">
                            {cost.lecturerPercentage || '60.00'}%
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(cost.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => {
                                setSelectedCost(cost);
                                setShowEditModal(true);
                              }}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 transition-colors"
                              title="Edit Pricing"
                            >
                              <Edit className="h-3.5 w-3.5 mr-1" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteCost(cost.id)}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 transition-colors"
                              title="Delete Pricing"
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-1" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        /* Grouped by Unit View (Default) */
        <div className="space-y-8">
          {Object.entries(groupedCosts).map(([unitName, costs]) => {
            const unit = units.find(u => u.name === unitName);
            const totalCost = costs.reduce((sum, cost) => sum + parseFloat(cost.cost), 0);
            const avgCost = totalCost / costs.length;

            return (
              <div key={unitName} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Unit Header */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white mr-3">
                        <BookOpen className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{unitName}</h3>
                        <p className="text-sm text-gray-600">{costs.length} campus{costs.length !== 1 ? 'es' : ''} • Avg: {formatCurrency(avgCost, costs[0]?.currency || 'KES')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">{formatCurrency(totalCost, costs[0]?.currency || 'KES')}</div>
                        <div className="text-xs text-gray-500">Total Value</div>
                      </div>
                      <TrendingUp className="h-5 w-5 text-green-500" />
                    </div>
                  </div>
                </div>

                {/* Campus Pricing List */}
                <div className="bg-white">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Campus
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cost
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Effective Dates
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tutor %
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {costs.map((cost, index) => {
                          const campus = campuses.find(c => c.id === cost.campusId);

                          return (
                            <tr key={cost.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white mr-3">
                                    <MapPin className="h-4 w-4" />
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {campus?.name || 'Unknown Campus'}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {campus?.location || ''}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className={`w-8 h-8 bg-gradient-to-r ${cost.isFree ? 'from-purple-500 to-indigo-600' : 'from-green-500 to-emerald-600'} rounded-lg flex items-center justify-center text-white mr-3`}>
                                    {cost.isFree ? <CheckCircle className="h-4 w-4" /> : <DollarSign className="h-4 w-4" />}
                                  </div>
                                  <div>
                                    <div className={`text-sm font-semibold ${cost.isFree ? 'text-indigo-600' : 'text-gray-900'}`}>
                                      {formatCurrency(cost.cost, cost.currency, cost.isFree)}
                                    </div>
                                    {cost.isFree ? (
                                      <div className="text-xs text-gray-500 italic max-w-[150px] truncate" title={cost.freeDescription}>
                                        {cost.freeDescription || 'No description'}
                                      </div>
                                    ) : (
                                      <div className="text-xs text-gray-500 uppercase">
                                        {cost.currency}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  <div className="flex items-center mb-1">
                                    <Calendar className="h-4 w-4 mr-2 text-green-500" />
                                    <span>From: {formatDate(cost.effectiveFrom)}</span>
                                  </div>
                                  {cost.effectiveTo && (
                                    <div className="flex items-center">
                                      <Calendar className="h-4 w-4 mr-2 text-red-500" />
                                      <span>To: {formatDate(cost.effectiveTo)}</span>
                                    </div>
                                  )}
                                  {!cost.effectiveTo && (
                                    <span className="text-xs text-green-600 font-medium">Ongoing</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center text-sm font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-lg w-fit">
                                  {cost.lecturerPercentage || '60.00'}%
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(cost.createdAt).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => {
                                      setSelectedCost(cost);
                                      setShowEditModal(true);
                                    }}
                                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 transition-colors"
                                    title="Edit Pricing"
                                  >
                                    <Edit className="h-3.5 w-3.5 mr-1" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteCost(cost.id)}
                                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 transition-colors"
                                    title="Delete Pricing"
                                  >
                                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Cost Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Add Unit Pricing</h2>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleCreateCost} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit
                </label>
                <select
                  value={createForm.unitId}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, unitId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a unit...</option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.id}>{unit.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campus
                </label>
                <select
                  value={createForm.campusId}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, campusId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a campus...</option>
                  {campuses.map((campus) => (
                    <option key={campus.id} value={campus.id}>{campus.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center mb-4 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                <input
                  type="checkbox"
                  id="create-isFree"
                  checked={createForm.isFree}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, isFree: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="create-isFree" className="ml-3 block text-sm font-bold text-indigo-900">
                  Mark as FREE Course
                </label>
              </div>

              {!createForm.isFree ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cost
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={createForm.cost}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, cost: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Currency
                    </label>
                    <select
                      value={createForm.currency}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, currency: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="KES">KES</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Free Course Description
                  </label>
                  <textarea
                    value={createForm.freeDescription}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, freeDescription: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Describe why this course is free (e.g., introductory unit, scholarship access...)"
                    rows={3}
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective From
                </label>
                <input
                  type="date"
                  value={createForm.effectiveFrom}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, effectiveFrom: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective To (Optional)
                </label>
                <input
                  type="date"
                  value={createForm.effectiveTo}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, effectiveTo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-blue-700 mb-1 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Tutor Earning Percentage (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={createForm.lecturerPercentage}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, lecturerPercentage: e.target.value }))}
                  className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-bold text-blue-600"
                  placeholder="60.00"
                  required
                />
                <p className="text-[10px] text-blue-500 mt-1 italic">Percentage of student payment that goes to the lecturer</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Create Pricing
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Create Modal */}
      {showBulkForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Bulk Set Pricing</h2>
              <button
                onClick={() => setShowBulkForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleBulkCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Units
                </label>

                {/* Select All Units Checkbox */}
                <div className="mb-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={bulkForm.selectAllUnits}
                      onChange={(e) => setBulkForm(prev => ({
                        ...prev,
                        selectAllUnits: e.target.checked,
                        unitIds: e.target.checked ? [] : prev.unitIds // Clear individual selections when select all is checked
                      }))}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Select All Units ({units.length} units)</span>
                  </label>
                </div>

                {/* Individual Unit Selection (only shown when Select All is not checked) */}
                {!bulkForm.selectAllUnits && (
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2">
                    {units.map((unit) => (
                      <label key={unit.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={bulkForm.unitIds.includes(unit.id.toString())}
                          onChange={(e) => {
                            const unitId = unit.id.toString();
                            setBulkForm(prev => ({
                              ...prev,
                              unitIds: e.target.checked
                                ? [...prev.unitIds, unitId]
                                : prev.unitIds.filter(id => id !== unitId)
                            }));
                          }}
                          className="mr-2"
                        />
                        {unit.name}
                      </label>
                    ))}
                  </div>
                )}

                {/* Show selected count */}
                <div className="mt-2 text-sm text-gray-600">
                  {bulkForm.selectAllUnits
                    ? `All ${units.length} units selected`
                    : `${bulkForm.unitIds.length} of ${units.length} units selected`
                  }
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campuses
                </label>
                <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2">
                  {campuses.map((campus) => (
                    <label key={campus.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={bulkForm.campusIds.includes(campus.id.toString())}
                        onChange={(e) => {
                          const campusId = campus.id.toString();
                          setBulkForm(prev => ({
                            ...prev,
                            campusIds: e.target.checked
                              ? [...prev.campusIds, campusId]
                              : prev.campusIds.filter(id => id !== campusId)
                          }));
                        }}
                        className="mr-2"
                      />
                      {campus.name}
                    </label>
                  ))}
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  {bulkForm.campusIds.length} of {campuses.length} campuses selected
                </div>
              </div>

              <div className="flex items-center mb-4 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                <input
                  type="checkbox"
                  id="bulk-isFree"
                  checked={bulkForm.isFree}
                  onChange={(e) => setBulkForm(prev => ({ ...prev, isFree: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="bulk-isFree" className="ml-3 block text-sm font-bold text-indigo-900">
                  Mark all as FREE Courses
                </label>
              </div>

              {!bulkForm.isFree ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cost
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={bulkForm.cost}
                      onChange={(e) => setBulkForm(prev => ({ ...prev, cost: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Currency
                    </label>
                    <select
                      value={bulkForm.currency}
                      onChange={(e) => setBulkForm(prev => ({ ...prev, currency: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="KES">KES</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Free Course Description
                  </label>
                  <textarea
                    value={bulkForm.freeDescription}
                    onChange={(e) => setBulkForm(prev => ({ ...prev, freeDescription: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Describe why these courses are free..."
                    rows={3}
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective From
                </label>
                <input
                  type="date"
                  value={bulkForm.effectiveFrom}
                  onChange={(e) => setBulkForm(prev => ({ ...prev, effectiveFrom: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective To (Optional)
                </label>
                <input
                  type="date"
                  value={bulkForm.effectiveTo}
                  onChange={(e) => setBulkForm(prev => ({ ...prev, effectiveTo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                <label className="block text-sm font-bold text-blue-800 mb-1 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Bulk Tutor Percentage (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={bulkForm.lecturerPercentage}
                  onChange={(e) => setBulkForm(prev => ({ ...prev, lecturerPercentage: e.target.value }))}
                  className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-bold text-blue-700"
                  placeholder="60.00"
                  required
                />
                <p className="text-[10px] text-blue-600 mt-1">This will apply to ALL selected units and campuses</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowBulkForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Set Pricing
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Cost Modal */}
      {showEditModal && selectedCost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Edit Unit Pricing</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleEditCost} className="space-y-4">
              <div className="flex items-center mb-4 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                <input
                  type="checkbox"
                  id="edit-isFree"
                  checked={selectedCost.isFree}
                  onChange={(e) => setSelectedCost(prev => ({ ...prev, isFree: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="edit-isFree" className="ml-3 block text-sm font-bold text-indigo-900">
                  Mark as FREE Course
                </label>
              </div>

              {!selectedCost.isFree ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cost
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={selectedCost.cost}
                      onChange={(e) => setSelectedCost(prev => ({ ...prev, cost: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Currency
                    </label>
                    <select
                      value={selectedCost.currency}
                      onChange={(e) => setSelectedCost(prev => ({ ...prev, currency: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="KES">KES</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="animate-in fade-in slide-in-from-top-1 duration-300">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Free Course Description
                  </label>
                  <textarea
                    value={selectedCost.freeDescription || ''}
                    onChange={(e) => setSelectedCost(prev => ({ ...prev, freeDescription: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Describe why this course is free..."
                    rows={3}
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective From
                </label>
                <input
                  type="date"
                  value={selectedCost.effectiveFrom ? selectedCost.effectiveFrom.split('T')[0] : ''}
                  onChange={(e) => setSelectedCost(prev => ({ ...prev, effectiveFrom: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective To (Optional)
                </label>
                <input
                  type="date"
                  value={selectedCost.effectiveTo ? selectedCost.effectiveTo.split('T')[0] : ''}
                  onChange={(e) => setSelectedCost(prev => ({ ...prev, effectiveTo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-blue-700 mb-1 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Tutor Earning Percentage (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={selectedCost.lecturerPercentage}
                  onChange={(e) => setSelectedCost(prev => ({ ...prev, lecturerPercentage: e.target.value }))}
                  className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-bold text-blue-600"
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Update Pricing
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredCosts.length === 0 && !loading && (
        <div className="text-center py-12">
          <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No pricing records found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || unitFilter || campusFilter
              ? 'Try adjusting your search and filter criteria.'
              : 'Get started by setting pricing for your units.'}
          </p>
          {!searchTerm && !unitFilter && !campusFilter && (
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setShowBulkForm(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Building className="h-4 w-4 mr-2" />
                Bulk Set Pricing
              </button>
              <button
                onClick={() => setShowCreateForm(true)}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Pricing
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
